import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfiguration } from '../../context/ConfigurationContext';
import { PLANS } from '../../constants';

/* ─────────────────────────────────────────────────────────────────────────────
   SAFE ZONE SETUP REMOVED — geofencing is CUT from v1.

   Feature 03 spec: "Do not create any geofence-related tables, code, or UI in
   v1. Postponed to Wave 1.5." It shipped on the live signup wizard anyway. PR
   #50 removed the dashboard drawer; this removes the other half — the step
   where the zone was first drawn.

   What was here: an enable toggle, a Leaflet map with click-to-place-home, a
   "Use My Location" button, 100m/500m/1km distance presets, and a
   24-7/night/day monitoring schedule. All of it is preserved in
   ./SafeZoneSetup.wave1-5.tsx, which nothing imports.

   Why moved out rather than wrapped in FEATURES.GEOFENCING_ENABLED: a flag
   wrapper hides the UI but leaves "Safe Zone Setup" / "Configuration de la
   zone securitaire" inside the production JS bundle, because the JSX is still
   reachable from an imported module. That is the lesson already recorded for
   GeofenceBreachAlert in src/constants/featureFlags.ts — gating alone does not
   tree-shake a component that is imported. Not importing it is what keeps the
   copy out of the bundle. The flag remains the Wave 1.5 switch; restoring the
   import is the other half of the flip.

   Also deleted, NOT parked: an on-mount navigator.geolocation.getCurrentPosition
   call that fired the browser's location permission prompt the instant this
   step rendered — before the caregiver enabled anything, for a feature that
   does not exist in v1. Do not reintroduce it.

   Restore steps for Wave 1.5 are at the top of SafeZoneSetup.wave1-5.tsx.
   ───────────────────────────────────────────────────────────────────────── */

interface FeatureSetupProps {
  onNext: () => void;
  onBack: () => void;
}

const FeatureSetup: FC<FeatureSetupProps> = ({ onNext, onBack }) => {
  const { i18n } = useTranslation();
  const isFr = i18n.language?.startsWith('fr');

  const { selectedPlan } = useConfiguration();

  const plan = PLANS.find(p => p.id === selectedPlan);

  const handleSaveAndContinue = () => {
    onNext();
  };

  // Fallback if plan not found (shouldn't happen)
  if (!plan) {
    return (
      <div className="min-h-screen bg-brand-bg dark:bg-slate-900 font-body pb-24">
        <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-brand-border dark:border-slate-800">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={onBack}
                className="flex items-center gap-2 text-brand-text-light hover:text-brand-text dark:text-slate-400 dark:hover:text-white transition-colors"
              >
                <span className="material-symbols-outlined">arrow_back</span>
                <span className="font-medium">{isFr ? 'Retour' : 'Back'}</span>
              </button>
              <span className="font-bold text-brand-text dark:text-white">{isFr ? 'Configuration des fonctions' : 'Feature Setup'}</span>
              <div className="text-sm text-brand-text-muted">{isFr ? '\u00c9tape 5 de 7' : 'Step 5 of 7'}</div>
            </div>
            <div className="mt-3 h-1.5 bg-brand-bg-warm dark:bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-brand-primary rounded-full transition-all duration-500" style={{ width: '71%' }} />
            </div>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12 text-center">
          <div className="w-20 h-20 rounded-full bg-brand-primary-light flex items-center justify-center mx-auto mb-6">
            <span className="material-symbols-outlined text-4xl text-brand-primary">check_circle</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-brand-text dark:text-white mb-4">
            {isFr ? 'Tout est pr\u00eat !' : "You're all set!"}
          </h1>
          <p className="text-lg text-brand-text-light dark:text-slate-400 mb-8">
            {isFr ? 'Votre forfait Essentiel inclut toutes les fonctions de base pour rester connect\u00e9.' : 'Your Essential plan includes all the core features needed to stay connected.'}
          </p>
        </div>
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-brand-border dark:border-slate-800 p-4">
          <div className="max-w-3xl mx-auto flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
            <button onClick={onBack} className="w-full sm:w-auto px-6 py-3 text-brand-text-light dark:text-slate-400 font-medium hover:text-brand-text dark:hover:text-white transition-colors">
              {isFr ? 'Retour' : 'Back'}
            </button>
            <button onClick={handleSaveAndContinue} className="w-full sm:w-auto px-8 py-3 bg-brand-accent text-white font-bold rounded-xl hover:bg-[#c96a3f] transition-all shadow-lg shadow-brand-accent/30">
              {isFr ? 'Terminer la configuration' : 'Complete Setup'}
              <span className="material-symbols-outlined ml-2 align-middle">check</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg dark:bg-slate-900 font-body pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-brand-border dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-brand-text-light hover:text-brand-text dark:text-slate-400 dark:hover:text-white transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              <span className="font-medium">{isFr ? 'Retour' : 'Back'}</span>
            </button>
            <span className="font-bold text-brand-text dark:text-white">{isFr ? 'Configurer les fonctions' : 'Configure Features'}</span>
            <div className="text-sm text-brand-text-muted">{isFr ? '\u00c9tape 5 de 7' : 'Step 5 of 7'}</div>
          </div>
          <div className="mt-3 h-1.5 bg-brand-bg-warm dark:bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-brand-primary rounded-full transition-all duration-500" style={{ width: '71%' }} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-brand-text dark:text-white mb-4">
            {isFr ? `Configurez les fonctions ${plan?.name}` : `Set up your ${plan?.name} features`}
          </h1>
          <p className="text-brand-text-light dark:text-slate-400">
            {isFr ? 'Configurez les fonctions de s\u00e9curit\u00e9 et de sant\u00e9 incluses dans votre forfait' : 'Configure the safety and health features included in your plan'}
          </p>
        </div>

        {/* Additional Features Preview */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 sm:p-6 border border-brand-border dark:border-slate-700">
          <h3 className="font-bold text-brand-text dark:text-white mb-4">
            {isFr ? `Autres fonctions ${plan?.name}` : `Other ${plan?.name} Features`}
          </h3>
          <p className="text-sm text-brand-text-muted mb-4">
            {isFr ? 'Ces fonctions sont activ\u00e9es automatiquement :' : 'These features are automatically enabled:'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            {/* REMOVED for launch: "Phone drop detection" / "D\u00e9tection de chute du
                t\u00e9l\u00e9phone" and "Voice distress detection" / "D\u00e9tection vocale de
                d\u00e9tresse". Both sat under the "automatically enabled" heading above
                while every fall/drop and distress code path ships disabled
                (FALL_DETECTION_ENABLED=false on server, web and Android release),
                so this screen promised a signing-up caregiver two features the
                product does not deliver. Restore only when the feature ships AND
                the FR wording is supplied by the CEO. */}
            <FeatureItem icon="notifications" text={isFr ? "Alertes d'inactivit\u00e9" : 'Inactivity alerts'} highlight />
            <FeatureItem icon="shield" text={isFr ? 'Blocage des appels frauduleux' : 'Scam call blocking'} highlight />
            <FeatureItem icon="dashboard" text={isFr ? 'Tableau de bord aidant' : 'Caregiver dashboard'} highlight />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-brand-border dark:border-slate-800 p-4">
        <div className="max-w-3xl mx-auto flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
          <button
            onClick={onBack}
            className="w-full sm:w-auto px-6 py-3 text-brand-text-light dark:text-slate-400 font-medium hover:text-brand-text dark:hover:text-white transition-colors"
          >
            {isFr ? 'Retour' : 'Back'}
          </button>
          {/* No `disabled` gate: the safe-zone controls were the only inputs on
              this step, and their removal takes the only half-filled state with
              them. Restore `disabled={!canContinue}` alongside the zone UI in
              Wave 1.5. */}
          <button
            onClick={handleSaveAndContinue}
            className="w-full sm:w-auto px-8 py-3 bg-brand-accent text-white font-bold rounded-xl hover:bg-[#c96a3f] transition-all shadow-lg shadow-brand-accent/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand-accent"
          >
            {isFr ? 'Terminer la configuration' : 'Complete Setup'}
            <span className="material-symbols-outlined ml-2 align-middle">check</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Feature item component
const FeatureItem: FC<{ icon: string; text: string; highlight?: boolean }> = ({
  icon,
  text,
  highlight = false,
}) => (
  <div className="flex items-center gap-3">
    <span className={`material-symbols-outlined text-lg ${highlight ? 'text-brand-primary' : 'text-brand-text-muted'}`}>
      {icon}
    </span>
    <span className={`text-sm ${highlight ? 'font-medium text-brand-text dark:text-white' : 'text-brand-text-light dark:text-slate-400'}`}>
      {text}
    </span>
  </div>
);

export default FeatureSetup;
